import { TaxIdType } from "@gamedev-web/one-gaming-crm";

import billingRulesJson from "../config/developmentHardwareBillingRules.json";
import { DeviceOrderingRegion } from "../models/developmentHardware";

export type BillingTaxField = "none" | "vatId" | "taxIdNumber";
export type BillingTaxRequirement = "none" | "optional" | "required";
export type BillingTaxIdTypeName = keyof typeof TaxIdType;
export type BillingRuleQuestion = "hasVatId" | "chileVatStatus";
export type ChileVatStatus = "" | "exempt" | "liable";

type BillingTaxIdRule = Readonly<{
  field: BillingTaxField;
  requirement: BillingTaxRequirement;
  fixedType?: BillingTaxIdTypeName;
  allowedTypes?: BillingTaxIdTypeName[];
  partnerSelectsType?: boolean;
}>;

type BillingAttachmentRule = Readonly<{
  condition: "always";
}>;

type BillingRuleFacts = Readonly<{
  isSoleProprietor: boolean;
  hasVatId?: boolean;
  chileVatStatus: ChileVatStatus;
  taxIdValidationMismatch: boolean;
}>;

type BillingRuleVariant = Readonly<{
  when: Readonly<{
    fact: keyof BillingRuleFacts;
    equals: boolean | ChileVatStatus;
  }>;
  apply: Readonly<{
    taxId?: BillingTaxIdRule;
    attachment?: BillingAttachmentRule;
    computedValues?: Readonly<{
      vatId?: string;
      taxIdType?: BillingTaxIdTypeName;
    }>;
  }>;
}>;

type BillingRuleDefinition = Readonly<{
  taxId: BillingTaxIdRule;
  questions?: BillingRuleQuestion[];
  variants?: BillingRuleVariant[];
  attachment?: BillingAttachmentRule;
  additionalDetails?: "chinaGoldenTax";
  computedValues?: Readonly<{
    vatId?: string;
    taxIdType?: BillingTaxIdTypeName;
  }>;
}>;

type BillingRulesConfig = Readonly<{
  schemaVersion: number;
  regionDefaults: Readonly<Record<DeviceOrderingRegion, BillingRuleDefinition>>;
  countryOverrides: Readonly<Record<string, Partial<BillingRuleDefinition>>>;
}>;

export type ResolvedBillingRule = Readonly<{
  taxId: BillingTaxIdRule;
  questions: BillingRuleQuestion[];
  attachment?: BillingAttachmentRule;
  additionalDetails?: "chinaGoldenTax";
  computedValues: Readonly<{
    vatId?: string;
    taxIdType?: TaxIdType;
  }>;
}>;

const billingRules = billingRulesJson as BillingRulesConfig;

const taxIdTypeValues: Readonly<Record<BillingTaxIdTypeName, TaxIdType>> = {
  BrazilCcm: TaxIdType.BrazilCcm,
  BrazilCnpj: TaxIdType.BrazilCnpj,
  IndiaGst: TaxIdType.IndiaGst,
  IndiaPan: TaxIdType.IndiaPan,
  VatId: TaxIdType.VatId,
};

const matchesVariant = (variant: BillingRuleVariant, facts: BillingRuleFacts): boolean =>
  facts[variant.when.fact] === variant.when.equals;

export const getBillingTaxIdTypeValue = (name: BillingTaxIdTypeName): TaxIdType =>
  taxIdTypeValues[name];

export const resolveDevelopmentHardwareBillingRule = ({
  isoCode,
  region,
  facts,
}: Readonly<{
  isoCode: string;
  region: DeviceOrderingRegion;
  facts: BillingRuleFacts;
}>): ResolvedBillingRule => {
  const regionRule = billingRules.regionDefaults[region];
  const countryRule = billingRules.countryOverrides[isoCode.toUpperCase()];
  const baseRule: BillingRuleDefinition = {
    ...regionRule,
    ...countryRule,
    taxId: countryRule?.taxId ?? regionRule.taxId,
    questions: countryRule?.questions ?? regionRule.questions,
    variants: countryRule?.variants ?? regionRule.variants,
    computedValues: {
      ...regionRule.computedValues,
      ...countryRule?.computedValues,
    },
  };
  const appliedRule = (baseRule.variants ?? [])
    .filter(variant => matchesVariant(variant, facts))
    .reduce<BillingRuleDefinition>(
      (current, variant) => ({
        ...current,
        ...variant.apply,
        taxId: variant.apply.taxId ?? current.taxId,
        computedValues: {
          ...current.computedValues,
          ...variant.apply.computedValues,
        },
      }),
      baseRule
    );

  return {
    taxId: appliedRule.taxId,
    questions: appliedRule.questions ?? [],
    attachment: appliedRule.attachment,
    additionalDetails: appliedRule.additionalDetails,
    computedValues: {
      vatId: appliedRule.computedValues?.vatId,
      taxIdType: appliedRule.computedValues?.taxIdType
        ? getBillingTaxIdTypeValue(appliedRule.computedValues.taxIdType)
        : appliedRule.taxId.fixedType
        ? getBillingTaxIdTypeValue(appliedRule.taxId.fixedType)
        : undefined,
    },
  };
};
